import { DocumentNode } from 'graphql';
import {
  IBuilder,
  IBuilderCohortConfig,
  ICohortConfigResponse,
  ICohortRow,
  IDataTableConfigResponse,
  IFunnelBuilderDefaultOptions,
  IFunnelBuilderSelectedOptions,
  IFunnelConfigResponse,
  IPathConfigResponse,
  IPredictionConfigResponse,
  IQueryConfigResponse,
  IRetentionConfigResponse,
  ISignificanceConfigResponse,
  RowType,
} from '../../redux/slices/oldBuilder';
import {
  FunnelModeKey,
  ICohortConfigFieldsFragment,
  IFunnelConfigFieldsFragment,
  QueryType,
  SimpleCohortTypeKey,
  TimeUnit,
  TimeWindowKey,
} from '../apollo/graphqlSchema';
import {
  defaultCohortRow,
  defaultRetention,
  defaultSampledCohort,
} from '../../redux/slices/oldBuilder/defaults';
import { assertNotNil } from '../types';
import {
  GET_DATA_TABLE_CONFIG,
  GET_FUNNEL_CONFIG,
  GET_PATH_CONFIG,
  GET_PREDICTION_CONFIG,
  GET_QUERY_CONFIG,
  GET_RETENTION_CONFIG,
  GET_SIGNIFICANCE_CONFIG,
} from '../apollo/builderConfig';
import {
  dataTableAdditionalSettingsTooltips,
  funnelAdditionalSettingsTooltips,
  pathAdditionalSettingsTooltips,
  retentionAdditionalSettingsTooltips,
} from '../../components/KubitTooltip/globalTooltips';
import {
  defaultCriteriaBySignificanceMode,
  defaultMetricBySignificanceMetricType,
  defaultSignificanceBuilderState,
} from '../../redux/slices/oldBuilder/significance/defaults';
import { ICachedCohortEntity } from '../../redux/slices/cachedEntities';
import getCreateCohortSubjectKey from './getCreateCohortSubjectKey';
import {
  defaultCohortDurationStart,
  defaultLookForwardWindow,
} from '../../redux/slices/oldBuilder/utils';
import { durationPartitionTimeUnits } from '../../redux/slices/oldBuilder/binning/defaults';
import { GET_COHORT_EXECUTE_CONFIG } from '../apollo/cohorts';
import { UnreachableCaseError } from 'ts-essentials';

/* Cohort V3: Due to the Graphql Validation rules, Caprica need to handle all operator keys like below */
export const parseCohortConfig = (
  cohortConfig: ICohortConfigFieldsFragment,
): IBuilderCohortConfig => {
  return {
    ...cohortConfig,
    expressionConfig: {
      ...cohortConfig.expressionConfig,
      operators: cohortConfig.expressionConfig.operators.map((operator) => ({
        key: operator.expressionKey,
        name: operator.name,
      })),
    },
    funnelConfig: {
      ...cohortConfig.funnelConfig,
      operators: cohortConfig.funnelConfig.operators.map((operator) => ({
        key: operator.funnelKey,
        name: operator.name,
      })),
      ttcConfig: {
        ...cohortConfig.funnelConfig.ttcConfig,
      },
    },
    measureConfig: {
      ...cohortConfig.measureConfig,
      operators: cohortConfig.measureConfig.operators.map((operator) => ({
        key: operator.measureKey,
        name: operator.name,
      })),
    },
    performedConfig: {
      ...cohortConfig.performedConfig,
      operators: cohortConfig.performedConfig.operators.map((operator) => ({
        key: operator.performedKey,
        name: operator.name,
      })),
    },
    retentionConfig: {
      ...cohortConfig.retentionConfig,
      operators: cohortConfig.retentionConfig.operators.map((operator) => ({
        key: operator.retentionKey,
        name: operator.name,
      })),
    },
  };
};

export const getCohortConfigValuesByType = (
  cohortConfig: IBuilderCohortConfig,
  simpleCohortTypeKey: SimpleCohortTypeKey,
) => {
  switch (simpleCohortTypeKey) {
    case SimpleCohortTypeKey.Expression:
      return cohortConfig.expressionConfig;
    case SimpleCohortTypeKey.Funnel:
      return cohortConfig.funnelConfig;
    case SimpleCohortTypeKey.Measure:
      return cohortConfig.measureConfig;
    case SimpleCohortTypeKey.Performed:
      return cohortConfig.performedConfig;
    case SimpleCohortTypeKey.Retention:
      return cohortConfig.retentionConfig;
    default:
      throw new UnreachableCaseError(simpleCohortTypeKey);
  }
};

export const getDefaultCohortValuesFromConfig = (
  cohortConfig: IBuilderCohortConfig,
  simpleCohortTypeKey?: SimpleCohortTypeKey,
): Pick<
  ICohortRow,
  'operator' | 'subject' | 'timeWindow' | 'simpleCohortTypeKey' | 'contextInput'
> => {
  const simpleCohortKey =
    simpleCohortTypeKey || defaultCohortRow().simpleCohortTypeKey;
  const configByConditionKey = getCohortConfigValuesByType(
    cohortConfig,
    simpleCohortKey,
  );

  const subjectFromConfig = configByConditionKey?.defaultSubject?.key ?? '';
  const operatorFromConfig = configByConditionKey?.operators?.[0]?.key ?? '';
  const timeWindowFromConfig =
    configByConditionKey?.defaultTimeWindow?.key ?? '';

  return {
    operator: operatorFromConfig,
    subject: subjectFromConfig,
    timeWindow: timeWindowFromConfig,
    contextInput: {
      __typename: 'CohortRefProps',
      samplePercentage:
        cohortConfig.defaultSamplingRate ||
        defaultCohortRow().contextInput.samplePercentage,
      dateRange: null,
    },
    simpleCohortTypeKey: simpleCohortKey,
  };
};

export const generateCohortRowFromCachedCohort = (
  cachedCohort: ICachedCohortEntity,
  cohortConfig: IBuilderCohortConfig,
) => {
  const configValues = getDefaultCohortValuesFromConfig(
    cohortConfig,
    cachedCohort.type,
  );

  const { subjects } = getCohortConfigValuesByType(
    cohortConfig,
    cachedCohort.type,
  );
  const subjectKey = configValues.subject
    ? getCreateCohortSubjectKey(
        configValues.subject,
        subjects,
        cachedCohort?.subjectKey,
      )
    : null;

  const builderCohort = {
    ...defaultCohortRow(),
    ...configValues,
    analysisId: cachedCohort.analysisId,
    measureId: cachedCohort.measureId,
    subject: subjectKey,
    operator: cachedCohort.operator,
    operatorValues: cachedCohort.operatorValues,
    isActive: true,
    datePicker: {
      __typename: 'DatePickerFixed',
      start: cachedCohort.startDate,
      end: cachedCohort.endDate,
    },
    cohortDurationStart:
      cachedCohort.cohortDurationStart ?? defaultCohortDurationStart,
    lookForwardWindow:
      cachedCohort.lookForwardWindow ?? defaultLookForwardWindow,
    timeToConvert: cachedCohort.timeToConvertInput
      ? {
          operatorValues: cachedCohort.timeToConvertInput.values,
          operator: cachedCohort.timeToConvertInput.operator,
          timeUnit: cachedCohort.timeToConvertInput.timeUnit,
        }
      : null,
  } satisfies ICohortRow;
  if (cachedCohort.type === SimpleCohortTypeKey.Retention) {
    builderCohort.timeWindow = TimeWindowKey.Overall;
    builderCohort.timeWindowValues = [null, null];
  }

  return builderCohort;
};

// In Cohort builder and Prediction we have cohorts that by default shown
// in Builder. We will use some values from cohort config as defaults so that
// user will not see empty fields.
export const getActiveCohortRowWithConfig = (
  cohortConfig: IBuilderCohortConfig,
  simpleCohortTypeKey?: SimpleCohortTypeKey,
): ICohortRow => {
  const configValues = getDefaultCohortValuesFromConfig(
    cohortConfig,
    simpleCohortTypeKey,
  );

  return {
    ...defaultCohortRow(),
    ...configValues,
    isActive: true,
  };
};

const parseQueryConfigValues = ({
  schemaId,
  queryConfig,
}: IQueryConfigResponse): IBuilder['initialConfig'] => {
  const {
    basicConfig,
    measureFunctions,
    measureRelOps,
    defaultTimeUnit,
    defaultFormulaSamplingRate,
    modes,
    defaultMode,
    modeConfigs,
  } = queryConfig;

  const {
    defaultStart,
    defaultEnd,
    liveStartDate,
    dateKinds,
    logicOps,
    cohortConfig: basicCohortConfig,
    defaultDatePicker,
  } = basicConfig;

  const units = dateKinds[0].supportedTimeUnits;

  const cohortConfig = parseCohortConfig(basicCohortConfig);

  const { impactConfig, histogramConfig } = modeConfigs;

  return {
    queryType: QueryType.Expression,
    units,
    defaultMeasureFunctions: measureFunctions,
    defaultMeasureRelOps: measureRelOps,
    dateConfig: {
      maxDate: defaultEnd,
      liveStartDate,
    },
    defaultLogOps: logicOps,
    activeSchemaId: schemaId,
    defaultOptions: {
      type: QueryType.Expression,
      dateKinds,
      modes,
      queryModeConfig: modeConfigs.queryConfig,
      impactModeConfig: impactConfig,
      histogramModeConfig: histogramConfig,
      samplingRate: defaultFormulaSamplingRate,
    },
    selectedOptions: {
      type: QueryType.Expression,
      subject: histogramConfig.defaultSubject.key,
      triggerSubject: impactConfig.defaultSubject.key,
      dateKind: dateKinds[0].key,
      mode: defaultMode.key,
      timeUnit: defaultTimeUnit,
      includeMissingDates: true,
    },
    // Cohort V3, apply default options to each cohort rows
    cohortConfig,
    defaultAdditionalSettings: [],
    dateInput: {
      startDate: defaultStart,
      endDate: defaultEnd,
      datePicker: defaultDatePicker,
    },
  };
};

const parseDataTableConfigValues = ({
  schemaId,
  dataTableConfig,
}: IDataTableConfigResponse): IBuilder['initialConfig'] => {
  const {
    basicConfig,
    measureFunctions,
    measureRelOps,
    subjects,
    defaultFormulaSamplingRate,
    settings,
  } = dataTableConfig;

  const {
    defaultStart,
    defaultEnd,
    liveStartDate,
    dateKinds,
    logicOps,
    cohortConfig: basicCohortConfig,
    defaultDatePicker,
  } = basicConfig;

  const units = dateKinds[0].supportedTimeUnits;

  const cohortConfig = parseCohortConfig(basicCohortConfig);

  return {
    queryType: QueryType.DataTable,
    units,
    defaultMeasureFunctions: measureFunctions,
    defaultMeasureRelOps: measureRelOps,
    dateConfig: {
      maxDate: defaultEnd,
      liveStartDate,
    },
    defaultLogOps: logicOps,
    activeSchemaId: schemaId,
    defaultOptions: {
      type: QueryType.DataTable,
      subjects,
      dateKinds,
      samplingRate: defaultFormulaSamplingRate,
    },
    selectedOptions: {
      type: QueryType.DataTable,
      dateKind: dateKinds[0].key,
    },
    // Cohort V3, apply default options to each cohort rows
    cohortConfig,
    defaultAdditionalSettings: settings.map((setting) => ({
      ...setting,
      tooltip: dataTableAdditionalSettingsTooltips[setting.key],
    })),
    dateInput: {
      startDate: defaultStart,
      endDate: defaultEnd,
      datePicker: defaultDatePicker,
    },
  };
};

const parseFunnelConfigValues = ({
  schemaId,
  funnelConfig,
}: IFunnelConfigResponse): IBuilder['initialConfig'] => {
  const { basicConfig, defaultMode, settings } = funnelConfig;
  const {
    defaultStart,
    defaultEnd,
    liveStartDate,
    logicOps,
    cohortConfig: basicCohortConfig,
    defaultDatePicker,
  } = basicConfig;

  const cohortConfig = parseCohortConfig(basicCohortConfig);

  const modeSpecificConfig = buildFunnelConfigForMode(
    funnelConfig,
    defaultMode.key,
  );

  return {
    ...modeSpecificConfig,
    queryType: QueryType.Funnel,
    activeSchemaId: schemaId,
    dateConfig: {
      maxDate: defaultEnd,
      liveStartDate,
    },
    defaultLogOps: logicOps,
    // Cohort V3, apply default options to each cohort rows
    cohortConfig,
    funnelConfig,
    dateInput: {
      startDate: defaultStart,
      endDate: defaultEnd,
      datePicker: defaultDatePicker,
    },
    defaultAdditionalSettings: settings.map((setting) => ({
      ...setting,
      tooltip: funnelAdditionalSettingsTooltips[setting.key],
    })),
  };
};

const parsePathConfigValues = ({
  schemaId,
  pathConfig,
}: IPathConfigResponse): IBuilder['initialConfig'] => {
  const {
    defaultStart,
    defaultEnd,
    liveStartDate,
    logicOps,
    subjects,
    partitions,
    defaultSubject,
    defaultPartition,
    cohortConfig: basicCohortConfig,
    groupbys,
    defaultGroupby,
    defaultFormulaSamplingRate,
    modes,
    defaultMode,
    settings,
    defaultDatePicker,
  } = pathConfig;

  const cohortConfig = parseCohortConfig(basicCohortConfig);

  return {
    queryType: QueryType.Path,
    dateConfig: {
      maxDate: defaultEnd,
      liveStartDate,
    },
    defaultLogOps: logicOps,
    activeSchemaId: schemaId,
    defaultOptions: {
      type: QueryType.Path,
      subjects,
      partitions,
      groupbys,
      partitionWindowTimeUnits: durationPartitionTimeUnits,
      defaultPartitionWindowTimeUnit: durationPartitionTimeUnits[0],
      modes,
      samplingRate: defaultFormulaSamplingRate,
    },
    selectedOptions: {
      type: QueryType.Path,
      subject: defaultSubject.key,
      partitionValues: [
        {
          __typename: 'PartitionValue',
          partition: defaultPartition,
          conversionWindow: null,
        },
      ],
      groupby: defaultGroupby.key,
      mode: defaultMode.key,
    },
    // Cohort V3, apply default options to each cohort rows
    cohortConfig,
    defaultAdditionalSettings: settings.map((setting) => ({
      ...setting,
      tooltip: pathAdditionalSettingsTooltips[setting.key],
    })),
    dateInput: {
      startDate: defaultStart,
      endDate: defaultEnd,
      datePicker: defaultDatePicker,
    },
  };
};
const parseRetentionConfigValues = ({
  schemaId,
  retentionConfig,
}: IRetentionConfigResponse): IBuilder['initialConfig'] => {
  const {
    defaultStart,
    defaultEnd,
    liveStartDate,
    logicOps,
    subjects,
    defaultSubject,
    cohortConfig: basicCohortConfig,
    defaultAttribution,
    attributions,
    groupbys,
    defaultGroupby,
    calculations,
    defaultCalculation,
    calculationWindows,
    defaultCalculationWindow,
    cohortUnits,
    retentionUnits,
    availableRetentionUnits,
    retentionViews,
    defaultRetentionView,
    defaultFormulaSamplingRate,
    settings,
    defaultDatePicker,
  } = retentionConfig;

  const cohortConfig = parseCohortConfig(basicCohortConfig);

  // IF all time is not first index, re-arrange it
  const customSort = (units: TimeUnit[]): TimeUnit[] => {
    if (units.includes(TimeUnit.AllTime)) {
      return [
        TimeUnit.AllTime,
        ...units.filter((unit) => unit !== TimeUnit.AllTime),
      ];
    }
    return units;
  };

  const sortedCohortUnits = customSort(cohortUnits);

  return {
    queryType: QueryType.Retention,
    dateConfig: {
      maxDate: defaultEnd,
      liveStartDate,
    },
    defaultLogOps: logicOps,
    activeSchemaId: schemaId,
    availableRetentionUnits,
    defaultOptions: {
      type: QueryType.Retention,
      subjects,
      attributions,
      groupbys,
      calculations,
      calculationWindows,
      retentionViews,
      defaultCohortUnits: sortedCohortUnits,
      defaultRetentionUnits: retentionUnits,
      samplingRate: defaultFormulaSamplingRate,
    },
    selectedOptions: {
      type: QueryType.Retention,
      subject: defaultSubject.key,
      attribution: defaultAttribution.key,
      groupby: defaultGroupby.key,
      calculation: defaultCalculation.key,
      calculationWindow: defaultCalculationWindow.key,
      retentionView: defaultRetentionView.key,
      cohortUnit: TimeUnit.Day,
    },
    retention: {
      ...defaultRetention,
    },
    // Cohort V3, apply default options to each cohort rows
    cohortConfig,
    timeWindow: 0,
    defaultAdditionalSettings: settings.map((setting) => ({
      ...setting,
      tooltip: retentionAdditionalSettingsTooltips[setting.key],
    })),
    dateInput: {
      startDate: defaultStart,
      endDate: defaultEnd,
      datePicker: defaultDatePicker,
    },
  };
};

const parsePredictionConfigValues = ({
  schemaId,
  predictionConfig,
}: IPredictionConfigResponse): IBuilder['initialConfig'] => {
  const {
    defaultStart,
    defaultEnd,
    liveStartDate,
    logicOps,
    cohortConfig: basicCohortConfig,
    defaultDatePicker,
  } = predictionConfig;

  const cohortConfig = parseCohortConfig(basicCohortConfig);
  const cohortRow = getActiveCohortRowWithConfig(cohortConfig);

  return {
    queryType: QueryType.Prediction,
    dateConfig: {
      maxDate: defaultEnd,
      liveStartDate,
    },
    defaultLogOps: logicOps,
    activeSchemaId: schemaId,
    // Cohort V3, apply default options to each cohort rows
    cohortConfig,
    [RowType.predictionCohorts]: [cohortRow, cohortRow],
    dateInput: {
      startDate: defaultStart,
      endDate: defaultEnd,
      datePicker: defaultDatePicker,
    },
  };
};

const parseSignificanceConfigValues = ({
  schemaId,
  significanceConfig,
}: ISignificanceConfigResponse): IBuilder['initialConfig'] => {
  const {
    cohortConfig: basicCohortConfig,
    defaultStart,
    defaultEnd,
    defaultSubject,
    defaultMetric,
    defaultMode,
    liveStartDate,
    subjects,
    defaultTest,
    tests,
    timezone,
    metrics,
    modes,
    experimentConfig,
    defaultDatePicker,
  } = significanceConfig;

  const cohortConfig = parseCohortConfig(basicCohortConfig);
  const queryType = QueryType.Significance;

  return {
    queryType,
    dateConfig: {
      maxDate: defaultEnd,
      liveStartDate,
    },
    timezone,
    defaultLogOps: basicCohortConfig.logicOps,
    cohortConfig,
    activeSchemaId: schemaId,
    defaultOptions: {
      type: queryType,
      subjects,
      modes,
      metrics,
      tests,
      experimentConfig,
    },
    selectedOptions: {
      type: queryType,
      subject: defaultSubject.key,
      mode: defaultMode.key,
      metric: defaultMetric.key,
      test: defaultTest.key,
    },
    significance: {
      ...defaultSignificanceBuilderState,
      criteria: defaultCriteriaBySignificanceMode[defaultMode.key],
      metric: defaultMetricBySignificanceMetricType[defaultMetric.key],
    },
    dateInput: {
      startDate: defaultStart,
      endDate: defaultEnd,
      datePicker: defaultDatePicker,
    },
  };
};

const parseCohortExecuteConfigValues = ({
  schemaId,
  cohortExecuteConfig,
}: ICohortConfigResponse): IBuilder['initialConfig'] => {
  const {
    cohortConfig: basicCohortConfig,
    defaultStart,
    defaultEnd,
    liveStartDate,
    timezone,
    operations,
    defaultOperation,
    defaultDatePicker,
  } = cohortExecuteConfig;

  const cohortConfig = parseCohortConfig(basicCohortConfig);
  const cohortRow = getActiveCohortRowWithConfig(cohortConfig);

  return {
    queryType: QueryType.CohortSample,
    builderSampledCohort: {
      ...defaultSampledCohort(),
      operations,
      defaultOperation,
      samplingRate: {
        ...defaultSampledCohort().samplingRate,
        samplePercentage: basicCohortConfig.defaultSamplingRate,
      },
    },
    dateConfig: {
      maxDate: defaultEnd,
      liveStartDate,
    },
    timezone,
    defaultLogOps: basicCohortConfig.logicOps,
    cohortConfig,
    activeSchemaId: schemaId,
    builderCohorts: [cohortRow],
    dateInput: {
      startDate: defaultStart,
      endDate: defaultEnd,
      datePicker: defaultDatePicker,
    },
  };
};

export const builderConfigByQueryType: Record<QueryType, DocumentNode> = {
  [QueryType.Expression]: GET_QUERY_CONFIG,
  [QueryType.Funnel]: GET_FUNNEL_CONFIG,
  [QueryType.Retention]: GET_RETENTION_CONFIG,
  [QueryType.Prediction]: GET_PREDICTION_CONFIG,
  [QueryType.Path]: GET_PATH_CONFIG,
  [QueryType.DataTable]: GET_DATA_TABLE_CONFIG,
  [QueryType.Significance]: GET_SIGNIFICANCE_CONFIG,
  [QueryType.CohortSample]: GET_COHORT_EXECUTE_CONFIG,
};

export const buildFunnelConfigForMode = (
  funnelConfig: IFunnelConfigFieldsFragment,
  modeKey: FunnelModeKey,
): {
  defaultOptions: IFunnelBuilderDefaultOptions;
  selectedOptions: IFunnelBuilderSelectedOptions;
} => {
  const modeConfig = funnelConfig.modeConfigs.find(
    (mc) => mc.mode.key === modeKey,
  );
  assertNotNil(modeConfig);

  return {
    defaultOptions: {
      type: QueryType.Funnel,
      // Mode config
      subjects: modeConfig.subjects,
      partitions: modeConfig.partitions,
      funnelOrders: modeConfig.orders,
      defaultPartitionWindowTimeUnit: modeConfig.defaultPartitionWindowTimeUnit,
      partitionWindowTimeUnits: modeConfig.partitionWindowTimeUnits,
      // Global config
      timeUnits: modeConfig.timeUnits,
      funnelCustomOrders: funnelConfig.customOrders,
      dateKinds: funnelConfig.basicConfig.dateKinds,
      groupbys: funnelConfig.groupbys,
      modes: funnelConfig.modes,
      samplingRate: funnelConfig.defaultFormulaSamplingRate,
    },
    selectedOptions: {
      type: QueryType.Funnel,
      // Mode config
      subject: modeConfig.defaultSubject.key,
      funnelOrder: modeConfig.defaultOrder.key,
      partitionValues: [modeConfig.defaultPartitionValue],
      // Global config
      dateKind: funnelConfig.basicConfig.dateKinds[0].key,
      funnelCustomOrder: funnelConfig.defaultCustomOrder.key,
      groupby: funnelConfig.defaultGroupby.key,
      mode: modeKey,
      timeUnit: modeConfig.defaultTimeUnit,
    },
  };
};

type BuilderInitialConfigResponse =
  | IQueryConfigResponse
  | IFunnelConfigResponse
  | IRetentionConfigResponse
  | IPredictionConfigResponse
  | IPathConfigResponse
  | IDataTableConfigResponse
  | ISignificanceConfigResponse
  | ICohortConfigResponse;

export const prepareBuilderInitialConfig = (
  config: BuilderInitialConfigResponse,
): IBuilder['initialConfig'] => {
  switch (config.queryType) {
    case QueryType.Expression: {
      return parseQueryConfigValues(config);
    }
    case QueryType.Funnel: {
      return parseFunnelConfigValues(config);
    }
    case QueryType.Retention: {
      return parseRetentionConfigValues(config);
    }
    case QueryType.Prediction: {
      return parsePredictionConfigValues(config);
    }
    case QueryType.Path: {
      return parsePathConfigValues(config);
    }
    case QueryType.DataTable: {
      return parseDataTableConfigValues(config);
    }
    case QueryType.Significance: {
      return parseSignificanceConfigValues(config);
    }
    case QueryType.CohortSample: {
      return parseCohortExecuteConfigValues(config);
    }
    default: {
      throw new UnreachableCaseError(config);
    }
  }
};
